import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Shield, Truck } from 'lucide-react';
import { Button } from '../components/ui/button';

const heroBackground = "https://static.prod-images.emergentagent.com/jobs/f7350cd1-72d3-4394-858a-2510c559bce3/images/2a6f2860fdb2ff9b0be58d35e1cc63014068a0b7299cfdc90497012fa2ed7cdc.png";
const boosterImage = "https://static.prod-images.emergentagent.com/jobs/f7350cd1-72d3-4394-858a-2510c559bce3/images/c3fe3b24a9be1f636b8b6fddd38d791792d804f9b9f86fb6eb97f4cd5e678f43.png";
const singlesImage = "https://static.prod-images.emergentagent.com/jobs/f7350cd1-72d3-4394-858a-2510c559bce3/images/503665898aabe2efa00c3bf71bac97399eb62422208c39ec4d5fe155e748dd4b.png";
const deckImage = "https://images.unsplash.com/photo-1742743031805-aa88ee5f300b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxUQ0clMjBzdGFydGVyJTIwZGVjayUyMHRyYWRpbmclMjBjYXJkJTIwZ2FtZSUyMGJveHxlbnwwfHx8fDE3NzUxNDc3NzF8MA&ixlib=rb-4.1.0&q=85&w=600";
const pokemonImage = "https://images.unsplash.com/photo-1647892591880-58c55fd726d8?w=600";
const mtgImage = "https://images.unsplash.com/photo-1513002433973-e0a181372d60?w=600";
const yugiohImage = "https://images.pexels.com/photos/16321207/pexels-photo-16321207.jpeg?w=600";

export const HomePage = () => {
  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Background */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroBackground})` }}
        >
          <div className="absolute inset-0 bg-black/70" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block mb-6">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-fuchsia-400 bg-fuchsia-500/10 px-4 py-2 rounded-full">
              La référence TCG en ligne
            </span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-6 font-outfit tracking-tight">
            Collectionnez.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-500 to-amber-400">
              Jouez. Gagnez.
            </span>
          </h1>
          
          <p className="text-lg sm:text-xl text-gray-300 max-w-2xl mx-auto mb-10">
            Découvrez notre sélection premium de cartes Yu-Gi-Oh!, Magic: The Gathering et Pokémon. 
            Boosters, decks et cartes rares à prix compétitifs.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/shop">
              <Button 
                size="lg" 
                className="bg-fuchsia-600 hover:bg-fuchsia-500 text-white px-8 py-6 text-lg glow-primary"
                data-testid="shop-now-btn"
              >
                Explorer la boutique
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/shop?category=single">
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white/20 text-white hover:bg-white/10 px-8 py-6 text-lg"
                data-testid="singles-btn"
              >
                Cartes rares
              </Button>
            </Link>
          </div>
        </div>

        {/* Floating Cards Animation */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex gap-4 opacity-50">
          <div className="w-2 h-2 bg-fuchsia-500 rounded-full animate-bounce" style={{ animationDelay: '0s' }} />
          <div className="w-2 h-2 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
          <div className="w-2 h-2 bg-fuchsia-500 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }} />
        </div>
      </section>

      {/* Categories Bento Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 font-outfit">
            Explorez nos catégories
          </h2>
          <p className="text-gray-400 max-w-xl mx-auto">
            Des milliers de cartes vous attendent
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Boosters */}
          <Link 
            to="/shop?category=booster" 
            className="group"
            data-testid="category-boosters"
          >
            <div className="relative h-80 rounded-2xl overflow-hidden card-hover">
              <img 
                src={boosterImage} 
                alt="Boosters" 
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <span className="text-fuchsia-400 text-sm font-semibold uppercase tracking-wider">Collection</span>
                <h3 className="text-3xl font-bold text-white mt-2 font-outfit">Boosters</h3>
                <p className="text-gray-300 mt-2">Ouvrez l'aventure</p>
              </div>
            </div>
          </Link>

          {/* Singles */}
          <Link 
            to="/shop?category=single" 
            className="group"
            data-testid="category-singles"
          >
            <div className="relative h-80 rounded-2xl overflow-hidden card-hover">
              <img 
                src={singlesImage} 
                alt="Cartes à l'unité" 
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <span className="text-amber-400 text-sm font-semibold uppercase tracking-wider">Rares</span>
                <h3 className="text-3xl font-bold text-white mt-2 font-outfit">Singles</h3>
                <p className="text-gray-300 mt-2">Trouvez LA carte</p>
              </div>
            </div>
          </Link>

          {/* Decks */}
          <Link 
            to="/shop?category=deck" 
            className="group"
            data-testid="category-decks"
          >
            <div className="relative h-80 rounded-2xl overflow-hidden card-hover">
              <img 
                src={deckImage} 
                alt="Decks" 
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <span className="text-green-400 text-sm font-semibold uppercase tracking-wider">Prêt à jouer</span>
                <h3 className="text-3xl font-bold text-white mt-2 font-outfit">Decks</h3>
                <p className="text-gray-300 mt-2">Jouez immédiatement</p>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* TCG Games Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 font-outfit">
              Vos jeux préférés
            </h2>
            <p className="text-gray-400 max-w-xl mx-auto">
              Les trois univers TCG les plus populaires au monde
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Yu-Gi-Oh! */}
            <Link 
              to="/shop?game=yugioh" 
              className="group"
              data-testid="game-yugioh"
            >
              <div className="relative h-96 rounded-2xl overflow-hidden card-hover border border-white/10">
                <img 
                  src={yugiohImage} 
                  alt="Yu-Gi-Oh!" 
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-orange-900/90 via-orange-900/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 text-center">
                  <h3 className="text-2xl font-bold text-white font-outfit">Yu-Gi-Oh!</h3>
                  <p className="text-orange-200 text-sm mt-2">Le roi des jeux</p>
                </div>
              </div>
            </Link>

            {/* Magic */}
            <Link 
              to="/shop?game=magic" 
              className="group"
              data-testid="game-magic"
            >
              <div className="relative h-96 rounded-2xl overflow-hidden card-hover border border-white/10">
                <img 
                  src={mtgImage} 
                  alt="Magic: The Gathering" 
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-purple-900/90 via-purple-900/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 text-center">
                  <h3 className="text-2xl font-bold text-white font-outfit">Magic</h3>
                  <p className="text-purple-200 text-sm mt-2">The Gathering</p>
                </div>
              </div>
            </Link>

            {/* Pokémon */}
            <Link 
              to="/shop?game=pokemon" 
              className="group"
              data-testid="game-pokemon"
            >
              <div className="relative h-96 rounded-2xl overflow-hidden card-hover border border-white/10">
                <img 
                  src={pokemonImage} 
                  alt="Pokémon" 
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-yellow-900/90 via-yellow-900/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 text-center">
                  <h3 className="text-2xl font-bold text-white font-outfit">Pokémon</h3>
                  <p className="text-yellow-200 text-sm mt-2">Attrapez-les tous</p>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="text-center p-8 rounded-2xl bg-[#121212] border border-white/10">
            <div className="w-14 h-14 mx-auto mb-6 rounded-full bg-fuchsia-500/20 flex items-center justify-center">
              <Sparkles className="w-7 h-7 text-fuchsia-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3 font-outfit">Cartes authentiques</h3>
            <p className="text-gray-400">100% originales, vérifiées par nos experts passionnés</p>
          </div>

          <div className="text-center p-8 rounded-2xl bg-[#121212] border border-white/10">
            <div className="w-14 h-14 mx-auto mb-6 rounded-full bg-amber-500/20 flex items-center justify-center">
              <Shield className="w-7 h-7 text-amber-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3 font-outfit">Paiement sécurisé</h3>
            <p className="text-gray-400">Stripe & PayPal pour des transactions en toute confiance</p>
          </div>

          <div className="text-center p-8 rounded-2xl bg-[#121212] border border-white/10">
            <div className="w-14 h-14 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
              <Truck className="w-7 h-7 text-green-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3 font-outfit">Livraison rapide</h3>
            <p className="text-gray-400">Expédition sous 24-48h, emballage protecteur</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-fuchsia-900/30 to-purple-900/30">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6 font-outfit">
            Prêt à commencer votre collection ?
          </h2>
          <p className="text-gray-300 mb-8">
            Rejoignez des milliers de collectionneurs passionnés
          </p>
          <Link to="/register">
            <Button 
              size="lg" 
              className="bg-fuchsia-600 hover:bg-fuchsia-500 text-white px-10 py-6 text-lg glow-primary"
              data-testid="cta-register-btn"
            >
              Créer un compte gratuit
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-white/10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <img src="https://customer-assets.emergentagent.com/job_fad4c604-2efd-4142-a35f-93aeb2be7f74/artifacts/hzrybxar_logo2.jpg" alt="Phoenix Card Game" className="w-8 h-8 rounded-lg object-cover" />
            <span className="text-white font-semibold font-outfit">Phoenix Card Game</span>
          </div>
          
          <p className="text-gray-500 text-sm">
            © 2024 Phoenix Card Game. Tous droits réservés.
          </p>
          
          <div className="flex items-center gap-6">
            <a href="#" className="text-gray-400 hover:text-white text-sm">CGV</a>
            <a href="#" className="text-gray-400 hover:text-white text-sm">Confidentialité</a>
            <a href="mailto:contact@phoenixcardgame.com" className="text-gray-400 hover:text-white text-sm">Contact</a>
            <a href="https://discord.gg/WB2EjyVz" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3 py-1.5 bg-[#5865F2]/10 border border-[#5865F2]/30 rounded-full hover:bg-[#5865F2]/20 transition-colors" data-testid="discord-link">
              <svg className="w-4 h-4 text-[#5865F2]" viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/></svg>
              <span className="text-[#5865F2] text-sm font-medium">Discord</span>
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};
